import { MapPin, Sparkles, Bot, UserCog, User } from "lucide-react";
import type { TheirStackAssignment, TheirStackTechManager, AssignmentStatus } from "../api/TheirStackJobsApi";
import { STATUS_OPTIONS } from "../api/TheirStackJobsApi";
import { STATUS_CHIP_SOLID, TERMINAL, daysAgo, formatPostedDate } from "./shared";
import CompanyApplyChip from "shared/CompanyApplyChip";
import RemoteScopeChip from "shared/RemoteScopeChip";
import TechTagChip from "shared/TechTagChip";
import StatusNotePopover from "shared/StatusNotePopover";
import StatusChangePopover from "shared/StatusChangePopover";
import ReassignPopover from "shared/ReassignPopover";

interface Props {
  assignment: TheirStackAssignment;
  showEmployee?: boolean; // admin view shows who it's assigned to
  employeeName?: string;
  techManagers?: TheirStackTechManager[];
  onReassign?: (assignmentId: number, employeeId: number) => Promise<void>;
  // Lets the status chip change status directly from the card, without opening the details modal.
  onStatusChange?: (assignmentId: number, status: AssignmentStatus, notes: string | null) => Promise<void>;
  onClick: () => void;
  // Slimmer layout for the Kanban board, where columns are narrow and already
  // convey status by grouping — drops the status chip, seniority badge, and
  // the assigned-by/enhanced badges in favor of fewer, tighter rows.
  compact?: boolean;
}

export default function AssignmentCard({ assignment, showEmployee, employeeName, techManagers, onReassign, onStatusChange, onClick, compact }: Props) {
  const statusEntry = STATUS_CHIP_SOLID[assignment.status];

  if (compact) {
    return (
      <div
        onClick={onClick}
        className="bg-surface/60 border border-border/60 rounded-xl p-3 cursor-pointer hover:border-border transition-all select-none"
      >
        <div className="flex items-start gap-2.5">
          <div className="flex flex-col items-center gap-1 shrink-0">
            <div className="w-8 h-8 rounded-lg border border-border bg-surface-muted flex items-center justify-center">
              <span className="text-xs font-bold text-primary font-inter">
                {assignment.company_name.charAt(0).toUpperCase()}
              </span>
            </div>
            {assignment.date_posted && (
              <span
                title={formatPostedDate(assignment.date_posted)}
                className="text-[9px] leading-none px-1.5 py-0.5 rounded-full border bg-blue-500 text-white border-blue-400 font-inter whitespace-nowrap animate-pulse"
              >
                {daysAgo(assignment.date_posted)}
              </span>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="text-xs font-semibold text-foreground font-inter leading-snug line-clamp-2">
              {assignment.job_title}
            </h3>
            <div className="flex items-center gap-1 mt-0.5 min-w-0">
              <span className="text-[11px] text-muted-foreground font-inter truncate">{assignment.company_name}</span>
              {assignment.location && (
                <>
                  <span className="text-muted-foreground text-[11px] shrink-0">•</span>
                  <span className="text-[11px] text-muted-foreground font-inter truncate">{assignment.location}</span>
                </>
              )}
            </div>
          </div>
        </div>

        {assignment.company_can_apply && (
          <div className="flex flex-wrap items-center gap-1 mt-2">
            <CompanyApplyChip companyCanApply={assignment.company_can_apply} metadataJson={assignment.company_analysis_metadata} />
          </div>
        )}

        <div className="flex items-center gap-1.5 mt-2">
          {assignment.employment_type && (
            <span className="text-[10px] px-1.5 py-0.5 rounded border bg-muted/50 text-muted-foreground border-border/40 font-inter shrink-0">
              {assignment.employment_type}
            </span>
          )}
          <TechTagChip label={assignment.stack_source} />
          <div className="ml-auto shrink-0">
            <RemoteScopeChip remoteScope={assignment.remote_scope} analysisJson={assignment.llm_analysis_metadata} />
          </div>
        </div>

        {showEmployee && employeeName && (
          <div className="flex items-center mt-2 pt-2 border-t border-border/40">
            {onReassign ? (
              <ReassignPopover
                candidates={techManagers ?? []}
                currentEmployeeId={assignment.employee_id}
                onReassign={(employeeId) => onReassign(assignment.assignment_id, employeeId)}
              >
                <span className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded border bg-muted/50 text-foreground border-border/40 font-inter truncate max-w-[120px]">
                  <User className="w-2.5 h-2.5 shrink-0" />
                  {employeeName}
                </span>
              </ReassignPopover>
            ) : (
              <span className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded border bg-muted/50 text-foreground border-border/40 font-inter truncate max-w-[120px]">
                <User className="w-2.5 h-2.5 shrink-0" />
                {employeeName}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      onClick={onClick}
      className="bg-surface/60 border border-border/60 rounded-xl p-4 cursor-pointer hover:border-border transition-all select-none"
    >
      <div className="flex items-start gap-3">
        <div className="flex flex-col items-center gap-1 shrink-0">
          <div className="w-10 h-10 rounded-lg border border-border bg-surface-muted flex items-center justify-center">
            <span className="text-sm font-bold text-primary font-inter">
              {assignment.company_name.charAt(0).toUpperCase()}
            </span>
          </div>
          {assignment.date_posted && (
            <span
              title={formatPostedDate(assignment.date_posted)}
              className="text-[9px] leading-none px-1.5 py-0.5 rounded-full border bg-blue-500 text-white border-blue-400 font-inter whitespace-nowrap animate-pulse"
            >
              {daysAgo(assignment.date_posted)}
            </span>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-sm font-semibold text-foreground font-inter leading-snug line-clamp-2">
              {assignment.job_title}
            </h3>
            <CompanyApplyChip companyCanApply={assignment.company_can_apply} metadataJson={assignment.company_analysis_metadata} />
            {statusEntry && (
              onStatusChange ? (
                <StatusChangePopover
                  options={STATUS_OPTIONS}
                  statusChipMap={STATUS_CHIP_SOLID}
                  currentStatus={assignment.status}
                  note={assignment.assignment_notes}
                  fromLabel={assignment.assignment_from_status ? STATUS_CHIP_SOLID[assignment.assignment_from_status]?.label : null}
                  disabled={TERMINAL.has(assignment.status)}
                  disabledMessage="This assignment is in a terminal state and cannot be updated."
                  onChange={(status, notes) => onStatusChange(assignment.assignment_id, status as AssignmentStatus, notes)}
                >
                  <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded border font-inter font-medium ${statusEntry.className}`}>
                    {statusEntry.label}
                  </span>
                </StatusChangePopover>
              ) : (
                <StatusNotePopover
                  note={assignment.assignment_notes}
                  fromLabel={assignment.assignment_from_status ? STATUS_CHIP_SOLID[assignment.assignment_from_status]?.label : null}
                  toLabel={statusEntry.label}
                >
                  <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded border font-inter font-medium ${statusEntry.className} ${assignment.assignment_notes ? "cursor-help" : ""}`}>
                    {statusEntry.label}
                  </span>
                </StatusNotePopover>
              )
            )}
          </div>

          <div className="flex items-center gap-1.5 mt-0.5 min-w-0">
            <span className="text-xs text-muted-foreground font-inter truncate">{assignment.company_name}</span>
            {assignment.location && (
              <>
                <span className="text-muted-foreground text-xs shrink-0">•</span>
                <span className="flex items-center gap-0.5 text-xs text-muted-foreground font-inter truncate shrink-0">
                  <MapPin className="w-3 h-3 shrink-0" />
                  {assignment.location}
                </span>
              </>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-1.5 mt-1">
            {assignment.seniority && (
              <span className="text-[10px] px-1.5 py-0.5 rounded border bg-muted/50 text-muted-foreground border-border/40 font-inter">
                {assignment.seniority}
              </span>
            )}
            {assignment.employment_type && (
              <span className="text-[10px] px-1.5 py-0.5 rounded border bg-muted/50 text-muted-foreground border-border/40 font-inter">
                {assignment.employment_type}
              </span>
            )}
            {/* assigned_by badge */}
            <span
              className={`flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded border font-inter ${
                assignment.assigned_by === "auto"
                  ? "bg-violet-500/10 text-violet-400 border-violet-500/20"
                  : "bg-indigo-500/10 text-indigo-400 border-indigo-500/20"
              }`}
            >
              {assignment.assigned_by === "auto" ? (
                <Bot className="w-2.5 h-2.5" />
              ) : (
                <UserCog className="w-2.5 h-2.5" />
              )}
              {assignment.assigned_by === "auto" ? "Auto" : "Manual"}
            </span>
            {assignment.has_enhancement && (
              <span className="flex items-center gap-0.5 text-[10px] text-emerald-400 font-inter">
                <Sparkles className="w-2.5 h-2.5" />
                Enhanced
              </span>
            )}
            <div className="ml-auto flex items-center gap-1.5">
              <RemoteScopeChip remoteScope={assignment.remote_scope} analysisJson={assignment.llm_analysis_metadata} />
            </div>
          </div>

          {/* Tech tag — flickering, placed last so it doesn't compete with regular metadata */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-2">
            <TechTagChip label={assignment.stack_source} />
            {showEmployee && employeeName && (
              <div className="ml-auto">
                {onReassign ? (
                  <ReassignPopover
                    candidates={techManagers ?? []}
                    currentEmployeeId={assignment.employee_id}
                    onReassign={(employeeId) => onReassign(assignment.assignment_id, employeeId)}
                  >
                    <span className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded border bg-muted/50 text-foreground border-border/40 font-inter truncate max-w-[120px]">
                      <User className="w-2.5 h-2.5 shrink-0" />
                      {employeeName}
                    </span>
                  </ReassignPopover>
                ) : (
                  <span className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded border bg-muted/50 text-foreground border-border/40 font-inter truncate max-w-[120px]">
                    <User className="w-2.5 h-2.5 shrink-0" />
                    {employeeName}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
